import {filter, map, mergeMap, mergeMapTo, shareReplay, tap} from 'rxjs/operators';
import {defer, merge, Observable, of, zip} from 'rxjs';

import LocalStorageService from 'web-app/services/local-storage';
import {
    ACCESS_TOKEN_KEY,
    EXPECTED_EXPIRATION_TIMESTAMP_KEY,
    isAccessTokenExpired,
    TOKEN_REFRESH_TIMESTAMP_KEY,
} from 'web-app/util/access-token-helpers';
import {didReceiveAccessToken} from 'web-app/react/session/actions';
import initializeEntities from 'web-app/react/setup-entities';
import {DID_RECEIVE_ACCESS_TOKEN} from 'web-app/react/constants/action-types';
import store from 'web-app/react/redux/store';
import {subscribeToStore} from 'web-app/react/components/higher-order/update-on-actions';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {getQueryParams} from 'web-app/util/url';
import {overrideWhitelabel, whitelabels} from 'web-app/shared/white-labeling-helpers';

import {withTranslationsReady$} from './with-translations';

// Type fluff to make observables nice and clean
type InvalidToken = ['access-token-invalid'];
type ValidToken = ['access-token-OK', string];
type TokenStatus = InvalidToken | ValidToken;
const isInvalidToken = (status: TokenStatus): status is InvalidToken => status[0] === 'access-token-invalid';
const isValidToken = (status: TokenStatus): status is ValidToken => status[0] === 'access-token-OK';

const getAccessToken = () => LocalStorageService.getFromLocalStorage(ACCESS_TOKEN_KEY) as string | undefined;

// Determine if stored access token is OK or not
const withTokenStatus$: Observable<InvalidToken | ValidToken> = zip(
    defer(() => of(getAccessToken())),
    defer(() => of(isAccessTokenExpired())),
).pipe(
    map(([accessToken, accessTokenExpired]) => {
        if (accessTokenExpired || !hasValue(accessToken)) {
            return ['access-token-invalid'];
        }
        return ['access-token-OK', accessToken];
    }),
);

// Clear or dispatch access token
export const withAccessTokenResolved$ = merge(
    withTokenStatus$.pipe(
        filter(isInvalidToken),
        tap(() => {
            LocalStorageService.clearKey(TOKEN_REFRESH_TIMESTAMP_KEY);
            LocalStorageService.clearKey(EXPECTED_EXPIRATION_TIMESTAMP_KEY);
            LocalStorageService.clearKey(ACCESS_TOKEN_KEY);
        }),
        map(([status]) => status),
    ),
    withTokenStatus$.pipe(
        filter(isValidToken),
        // Using mergeMapTo in order to only create inner observable _once_ otherwise shareReplay won't work
        mergeMapTo(
            // Using defer to read access token when emits pass this point
            defer(() => of(getAccessToken())).pipe(
                filter(hasValue),
                mergeMap(
                    token =>
                        new Observable<'access-token-OK'>(observer => {
                            const unsubscribe = subscribeToStore([DID_RECEIVE_ACCESS_TOKEN], () => {
                                observer.next('access-token-OK');
                                observer.complete();
                                // Unsubscribe as soon as we pick up the action
                                unsubscribe();
                            });
                            store.dispatch(didReceiveAccessToken(token));
                        }),
                ),
                // Using share replay here allows us to emit `access-token-OK` to several subscribers without
                // firing the previous observable again (which would dispatch didReceiveAccessToken again).
                // shareReplay is placed here in the stream so the action is only fired once.
                shareReplay(),
            ),
        ),
    ),
);

// Overrides whitelabel if query param "whitelabel" is set
const withWhitelabellingApplied$ = defer(() => of(getQueryParams())).pipe(
    // Unpack whitelabel query params
    map(([, params]) => params.get('whitelabel')),
    mergeMap(whitelabelKey => {
        // Early exit if param is not set
        if (!hasValue(whitelabelKey)) {
            return of(null);
        }

        if (!whitelabels.includes(whitelabelKey as any)) {
            return of(null);
        }

        // Finally override the whitelabel
        return of(whitelabelKey).pipe(tap(overrideWhitelabel));
    }),
);

/**
 * A shared observable that is currently used to control the rendering of React and Ember.
 * It ensures nothing can render until the source emits:
 * - withTranslationsReady$ ensures i18next is set up, otherwise translations would fall back to their keys
 * - withAccessTokenResolved$ ensures the access token is either OK or invalid.
 *   If it's OK it emits only when the key is actually in Redux.
 * - withWhitelabellingApplied$ ensures we've overriden the whitelabel if the query param "whitelabel" exists and is valid
 *
 * Please note that this is used in both Ember and React at the "mount point" of both implementations.
 * The idea is that this should be run as early is possible in the app lifecycle, and by putting
 * it both in Ember and React we've created a clear migration strategy. Once Ember is gone and we mount the app
 * via app/react/routes/application.tsx things should still work 🤞
 */
export const withApplicationReady$ = zip(
    withTranslationsReady$,
    withAccessTokenResolved$,
    withWhitelabellingApplied$,
).pipe(
    tap(() => {
        // Side-effect ported from old ember route
        initializeEntities();
    }),
    // shareReplay makes it possible to re-use this while only resolving the source once
    shareReplay(),
);
